import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { DTOBexioSendInvoiceReq } from "components/ImportData/type";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { DTOBexioGETContact, DTOBexioGetInvoice } from "../types";
import type { TFetchStatus } from "./types";

export interface BexioState {
    bexioContacts: DTOBexioGETContact[];
    fetchBexioContactsStatus: TFetchStatus;
    createdInvoice: Partial<DTOBexioGetInvoice>;
    failureReasonOnCreateInvoice: string;
}

export const fetchBexioContacts = createAsyncThunk("channels/fetchBexioContacts", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/integrations/bexio/contact`);
        return response.data.data as Array<DTOBexioGETContact>;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

/**
 * Handle to send a Bexio invoice.
 */
const sendInvoice = async (
    id: number,
    { recipient_email, subject, message, mark_as_open, attach_pdf }: DTOBexioSendInvoiceReq,
): Promise<boolean> => {
    const res = await axios.post<boolean>(
        import.meta.env.REACT_APP_API_ENDPOINT + `/integrations/bexio/invoice/${id}/send-email`,
        {
            recipient_email,
            subject,
            message,
            mark_as_open,
            attach_pdf,
        },
    );

    return res.data;
};

export const createBexioInvoice = createAsyncThunk(
    "bexio/createInvoice",
    async (
        {
            contactId,
            processingJobId,
            importId,
            calculationMethodForInvoice,
        }: {
            contactId: number;
            processingJobId: number;
            importId: number;
            calculationMethodForInvoice: "crealo" | "file";
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.post<{ data: DTOBexioGetInvoice }>(
                    import.meta.env.REACT_APP_API_ENDPOINT + "/integrations/bexio/invoice/create-invoice-from-file",
                    {
                        contactId,
                        processingJobId,
                        importId,
                        calculationMethodForInvoice,
                    },
                ),
                {
                    pending: t("Invoice.Pending"),
                    success: t("Invoice.Created"),
                    error: t("Invoice.Error"),
                },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

const initialState: BexioState = {
    bexioContacts: [],
    fetchBexioContactsStatus: "idle",
    createdInvoice: {} as Partial<DTOBexioGetInvoice>,
    failureReasonOnCreateInvoice: "",
};

export const bexioStore = createSlice({
    name: "bexio",
    initialState,
    reducers: {
        clearInvoice(state) {
            state.createdInvoice = {};
            state.failureReasonOnCreateInvoice = "";
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBexioContacts.fulfilled, (state, action) => {
            state.fetchBexioContactsStatus = "succeeded";
            state.bexioContacts = action.payload;
        });
        builder.addCase(fetchBexioContacts.pending, (state) => {
            state.fetchBexioContactsStatus = "pending";
            state.bexioContacts = [];
        });
        builder.addCase(fetchBexioContacts.rejected, (state, { payload }) => {
            state.fetchBexioContactsStatus = "failed";
            // No error thrown, not everybody is using it
        });

        builder.addCase(createBexioInvoice.pending, (state) => {
            state.createdInvoice = {};
            state.failureReasonOnCreateInvoice = "";
        });
        builder.addCase(createBexioInvoice.fulfilled, (state, { payload }) => {
            state.createdInvoice = payload;
        });
        builder.addCase(createBexioInvoice.rejected, (state, { payload }) => {
            state.failureReasonOnCreateInvoice = (payload as { data: string; message: string }).data;
        });
    },
});

export const { clearInvoice } = bexioStore.actions;

export default bexioStore.reducer;
